import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

const MAX_ATTEMPTS = 6;
const INTERVAL = 2500;

export default function BookingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | failed | timeout
  const [booking, setBooking] = useState(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    if (startedRef.current) return;
    startedRef.current = true;

    let attempts = 0;
    const poll = async () => {
      try {
        const res = await api.get(`/bookings/status/${sessionId}`);
        setBooking(res.data.booking);
        if (res.data.payment_status === "paid") { setState("paid"); return; }
        if (res.data.status === "expired") { setState("failed"); return; }
      } catch (e) { /* keep polling */ }
      attempts += 1;
      if (attempts >= MAX_ATTEMPTS) { setState("timeout"); return; }
      setTimeout(poll, INTERVAL);
    };
    poll();
  }, [sessionId]);

  return (
    <div className="App bg-white">
      <Navbar />
      <div className="min-h-screen pt-16 md:pt-20 flex items-center justify-center px-6">
        <div className="max-w-xl w-full text-center py-20" data-testid="booking-result">
          {state === "checking" && (
            <>
              <Loader2 size={56} className="mx-auto text-signal animate-spin" />
              <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tighter mt-8">Confirming your payment…</h1>
              <p className="text-muted-foreground mt-3">Hang tight, this only takes a moment.</p>
            </>
          )}

          {state === "paid" && (
            <div className="fade-up">
              <CheckCircle2 size={56} className="mx-auto text-signal" />
              <p className="text-signal font-mono text-xs uppercase tracking-[0.3em] mt-8">Booking confirmed</p>
              <h1 className="font-display font-black text-4xl md:text-6xl tracking-tighter mt-3" data-testid="success-heading">
                You're booked!
              </h1>
              {booking && (
                <div className="border border-border mt-10 text-left">
                  {[
                    ["Tour", booking.tour_name],
                    ["Date", booking.date],
                    ["Time", booking.time_slot],
                    ["Riders", booking.num_people],
                    ["Total paid", `$${Number(booking.amount).toFixed(2)} CAD`],
                  ].map(([k, v]) => (
                    <div key={k} className="flex justify-between px-6 py-4 border-b border-border last:border-b-0">
                      <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">{k}</span>
                      <span className="font-display font-semibold">{v}</span>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-muted-foreground mt-6 text-sm">A confirmation has been sent to your email. Please arrive 15 minutes early.</p>
              <Link to="/" className="inline-block mt-8 bg-void text-white px-8 py-4 text-sm font-bold uppercase tracking-widest hover:bg-signal transition-colors duration-200">
                Back to home
              </Link>
            </div>
          )}

          {(state === "failed" || state === "timeout") && (
            <>
              <XCircle size={56} className="mx-auto text-destructive" />
              <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tighter mt-8" data-testid="failure-heading">
                {state === "timeout" ? "Still processing" : "Payment not completed"}
              </h1>
              <p className="text-muted-foreground mt-3">
                {state === "timeout"
                  ? "We couldn't confirm your payment in time. Check your email or contact us if you were charged."
                  : "Your booking was not completed. No worries — you can try again."}
              </p>
              <Link to="/#tours" className="inline-block mt-8 bg-signal text-white px-8 py-4 text-sm font-bold uppercase tracking-widest hover:bg-void transition-colors duration-200">
                View tours
              </Link>
            </>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
